import { Component, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { NavigationEnd, Router } from '@angular/router';
import { TranslocoService } from '@jsverse/transloco';
import moment from 'moment';
import { LoginDialogComponent } from './components/login-dialog/login-dialog.component';
import { ContactModel, ContactResultModel } from './models/contactModels';
import { AuthService } from './services/auth.service';
import { CJHubService } from './services/cjhub.service';
import { ContactService } from './services/contact.service';
import { MyMonitoringService } from './services/logging.service';
import { StateService } from './services/state.service';
import { BookingConfigService } from './parkifly-bookings/services/booking-config.service';
import { Subject, takeUntil } from 'rxjs';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.css'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class AppComponent implements OnInit {
  public contactModel: ContactModel = new ContactModel();
  public messageSent: boolean;
  public messageIsBeeingSent = false;
  private userAboutToGetTimedOutWarningIsDisplayed = false;
  public tokenExpiresIn: any;
  private destroyed$ = new Subject<void>();
  public showNavigation = true;
  public currentBookingType: string = "advanced";
  private widgetResizeObserver?: ResizeObserver;
  private widgetMutationObserver?: MutationObserver;
  private lastSentHeight = 0;

  constructor(
    public authService: AuthService,
    public stateService: StateService,
    public cjHubService: CJHubService,
    public contactService: ContactService,
    public dialog: MatDialog,
    public snackBar: MatSnackBar,
    public router: Router,
    public translate: TranslocoService,
    private myMonitoringService: MyMonitoringService,
    private bookingConfigService: BookingConfigService,
  ) {
    translate.setDefaultLang('sv');
    translate.setActiveLang('sv');
  }

  ngOnInit(): void {
    if (window.self !== window.top) {
      document.documentElement.classList.add('iframe-layout');
    }
    this.checkQueryParams();
    this.tokenExpiresIn = this.tokenExpires();
    this.initUserTimedOut();
    this.initUserAboutToGetTimed();
    this.myMonitoringService.logTrace('Shuttlebuss Frontend App Started');
    this.router.events
      .pipe(takeUntil(this.destroyed$))
      .subscribe(async (event) => {
        if (event instanceof NavigationEnd) {
          this.bookingConfigService.setOrganizationFromUrl();
          this.checkUrl();
          if (window.self !== window.top) {
            this.setupWidgetResizeObserver();
          }
        }
      });

    setTimeout(() => {
      this.checkUrl();
      if (window.self !== window.top) {
        this.setupWidgetResizeObserver();
      }
    }, 0);
  }

  checkUrl() {
    const currentUrl = this.router.url;
    if (
      currentUrl.includes('/simple-widget-booking') ||
      currentUrl.includes('/advanced-widget-booking') ||
      currentUrl.includes('/short-term-widget-booking') ||
      currentUrl.includes('/app-short-term-widget') ||
      currentUrl.includes('/aiden-short-term-widget-booking') ||
      currentUrl.includes('/app-aiden-short-term-widget') ||
      currentUrl.includes('/park-and-fly-advanced-widget-booking') ||
      currentUrl.includes('/aiden-park-and-fly-advanced-widget-booking')
    ) {
      this.showNavigation = false;
      if (currentUrl.includes('/simple-widget-booking')) {
        this.currentBookingType = "simple";
      }
      if (currentUrl.includes('/advanced-widget-booking')) {
        this.currentBookingType = "advanced";
      }
      if (currentUrl.includes('/short-term-widget-booking') || currentUrl.includes('/app-short-term-widget')) {
        this.currentBookingType = "short-term";
      }
      if (currentUrl.includes('/aiden-short-term-widget-booking') || currentUrl.includes('/app-aiden-short-term-widget')) {
        this.currentBookingType = "aiden-short-term";
      }
      if (currentUrl.includes('/park-and-fly-advanced-widget-booking')) {
        this.currentBookingType = "park-and-fly-advanced";
      }
      if (currentUrl.includes('/aiden-park-and-fly-advanced-widget-booking')) {
        this.currentBookingType = "aiden-park-and-fly-advanced";
      }
    } else {
      this.showNavigation = true;
    }
  }

  checkQueryParams() {
    const urlParams = new URLSearchParams(window.location.search);
    const gateId = urlParams.get('g');
    const licensePlate = urlParams.get('l');
    const paymentRequired = urlParams.get('paymentRequired');
    const vehicleOwner = urlParams.get('vehicleOwner');

    if (gateId) {
      const queryParams: any = { g: gateId, l: licensePlate };
      if (paymentRequired !== null) queryParams.paymentRequired = paymentRequired;
      if (vehicleOwner !== null) queryParams.vehicleOwner = vehicleOwner;

      const currentUrl = window.location.pathname;

      // Don't navigate if already on a details form page
      if (currentUrl.includes('drive-up-details-form') || currentUrl.includes('drive-in-details-form')) {
        return;
      }

      // Determine booking type from current URL
      if (currentUrl.includes('drive-in-booking')) {
        return;
      } else if (currentUrl.includes('drive-up-booking')) {
        return;
      } else {
        this.router.navigate(['/parkifly/drive-up-booking'], {
          queryParams
        });
      }
    }
  }

  tokenExpires() {
    const tokenTime = moment(this.authService.getTokenExpirationDate());
    const timeNow = moment(new Date());
    return timeNow.to(tokenTime);
  }

  initUserAboutToGetTimed() {
    this.authService.getUserAboutToGetTimedOutObservable().subscribe(show => {
      if (show) {
        if (!this.userAboutToGetTimedOutWarningIsDisplayed) {
          this.userAboutToGetTimedOutWarningIsDisplayed = true;
          const snack = this.snackBar.open(
            `${this.translate.translate('AUTH.USER_WILL_BE_LOGGED_OUT')} ${this.tokenExpiresIn}`,
            this.translate.translate('AUTH.EXTEND_LOGIN_TIME'),
            {
              verticalPosition: 'top'
            }
          );
          snack.onAction().subscribe(() => {
            this.stateService.refreshToken();
            this.userAboutToGetTimedOutWarningIsDisplayed = false;
          });
        }
      }
      if (!show) {
        if (this.userAboutToGetTimedOutWarningIsDisplayed) {
          this.snackBar.dismiss();
          this.userAboutToGetTimedOutWarningIsDisplayed = false;
        }
      }
    });
  }

  initUserTimedOut() {
    this.authService.getUserTimedOutObservable().subscribe(username => {
      this.dialog.open(LoginDialogComponent, {
        data: { reLogin: true, currentRoute: this.router.url, username },
        maxHeight: '95vh',
        maxWidth: '95vw',
        hasBackdrop: true,
        disableClose: true,
        backdropClass: 'bluredbackdrop'
      });
    });
  }

  contact() {
    this.messageIsBeeingSent = true;
    this.contactService
      .contact(
        this.contactModel.email,
        this.contactModel.firstname,
        this.contactModel.lastname,
        this.contactModel.phone,
        this.contactModel.message,
        this.contactModel.company,
        'en'
      )
      .subscribe(
        (result: ContactResultModel) => {
          console.log('CreateUserResult', result);
          if (result.success) {
            this.messageIsBeeingSent = false;
            this.messageSent = true;
          } else {
            this.messageIsBeeingSent = false;
            // alert('Message failed to send, check all required input fields');
          }
        },
        (error: ContactResultModel) => {
          this.messageIsBeeingSent = false;
          // alert('Message failed to send, server error');
        }
      );
  }

  ngOnDestroy() {
    this.destroyed$.next();
    this.destroyed$.complete();
    if (this.widgetResizeObserver) {
      this.widgetResizeObserver.disconnect();
    }
    if (this.widgetMutationObserver) {
      this.widgetMutationObserver.disconnect();
    }
  }

  private setupWidgetResizeObserver() {
    if (typeof ResizeObserver !== 'undefined' && !this.widgetResizeObserver) {
      // Force auto height inside iframes to prevent height feedbacks infinite loop
      document.documentElement.style.setProperty('height', 'auto', 'important');
      document.documentElement.style.setProperty('min-height', 'auto', 'important');
      document.body.style.setProperty('height', 'auto', 'important');
      document.body.style.setProperty('min-height', 'auto', 'important');

      const checkAndPostHeight = () => {
        const appRoot = document.querySelector('app-root');
        let height = Math.max(
          document.body.scrollHeight,
          (appRoot as HTMLElement)?.scrollHeight || 0
        );

        // Support dynamic sizing for overlays (models, dropdown menus)
        const overlayPanes = document.querySelectorAll('.cdk-overlay-pane');
        overlayPanes.forEach((pane) => {
          const rect = pane.getBoundingClientRect();
          if (rect.height > 0 && rect.bottom > 0) {
            const paneBottom = window.scrollY + rect.top + rect.height;
            if (paneBottom > height) {
              height = paneBottom;
            }
          }
        });

        // Prevent infinite postMessage rendering loops by checking change delta threshold
        if (Math.abs(height - this.lastSentHeight) > 3) {
          this.lastSentHeight = height;
          window.parent.postMessage({
            type: 'parkifly-resize-widget',
            height: height + 35,
            url: window.location.href
          }, '*');
        }
      };

      this.widgetResizeObserver = new ResizeObserver(checkAndPostHeight);
      this.widgetResizeObserver.observe(document.body);

      // Listen for overlay modifications (dialogs opening etc.) which are siblings on the body element
      if (typeof MutationObserver !== 'undefined') {
        this.widgetMutationObserver = new MutationObserver(checkAndPostHeight);
        this.widgetMutationObserver.observe(document.body, { childList: true, subtree: true });
      }
    }
  }

}
